"use client";

import Link from "next/link";
import { Instagram } from "lucide-react";
import Logo from "../ui/logo";
import { cn } from "@/lib/utils";

const SITEMAP_LINKS = [
  { name: "Work", href: "/work" },
  { name: "Kunst", href: "/art" },
  { name: "Store", href: "https://store.renehuemer.com" },
  { name: "About", href: "/about" },
  { name: "Contact", href: "/contact" },
];

const LEGAL_LINKS = [
  { name: "Terms of Service", href: "/terms-of-service" },
  { name: "Privacy Policy", href: "/privacy-policy" },
  { name: "Imprint", href: "/imprint" },
];

const SOCIAL_LINKS = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/rene_huemer/",
    icon: <Instagram />,
  },
];

interface FooterProps {
  ecommerceEnabled?: boolean;
}

const Footer = ({ ecommerceEnabled = false }: FooterProps) => {
  // Function to trigger the CookieBanner to re-open
  const handleManageCookies = () => {
    window.dispatchEvent(new Event('reopen-cookie-banner'));
  };

  return (
    <section className="mt-3 bg-gray-50 text-gray-800 md:py-5">
      <div className="container">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:gap-14">
          
          {/* Logo & Newsletter */}
          <div className="flex w-full flex-col divide-y md:w-auto md:flex-row md:items-center md:divide-x md:divide-y-0">
            <Logo wrapperClassName="w-28 md:pe-4 border-background/10 pb-4 md:pb-0 dark:[&_img]:invert" imageClassName="-ml-2.5" />
            <p className="pt-4 text-xl md:ps-4 md:pt-0">
              <Link
                href="https://renehuemer.us14.list-manage.com/subscribe?u=c97467f80eb04db848de0af80&id=a2b8f32d84"
                className="text-gray-500 opacity-100 transition-opacity hover:opacity-80"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Newsletter"
              >
                Newsletter
              </Link>
            </p>
          </div>

          {/* Menu Sections */}
          <div className="grid flex-1 justify-center gap-6 md:flex md:grid-cols-2 md:gap-20 lg:gap-24">
            
            {/* Sitemap Column */}
            <div className="min-w-[120px]">
              <h3 className="text-xl font-medium">Sitemap</h3>
              <ul className="mt-3 space-y-2">
                {SITEMAP_LINKS.map((link, idx) => (
                  <li key={idx} className="transition-opacity hover:opacity-80">
                    <Link href={link.href}>{link.name}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Column */}
            <div className="min-w-[120px]">
  <h3 className="text-xl font-medium">Legal</h3>
  <ul className="mt-3 space-y-2">
    {LEGAL_LINKS.map((link, idx) => (
      <li key={idx} className="transition-opacity hover:opacity-80">
        <Link href={link.href}>{link.name}</Link>
      </li>
    ))}
    
    {/* Cookie Settings Button - only shown when ecommerce is enabled */}
    {ecommerceEnabled && (
      <li className="transition-opacity hover:opacity-80">
        <button
          onClick={handleManageCookies}
          className="text-left bg-transparent p-0 border-none cursor-pointer"
        >
          Cookie Settings
        </button>
      </li>
    )}
  </ul>
</div>

          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 flex flex-col justify-between border-t border-gray-200 pt-6 gap-6 md:flex-row">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} René Huemer. All rights reserved.
          </p>

          <div className="flex gap-4">
            {SOCIAL_LINKS.map((link, index) => (
              <Link
                key={index}
                href={link.href}
                className="text-gray-500 transition-opacity hover:opacity-80"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={link.name}
              >
                {link.icon}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Footer;
